/**
 * RewardCard — belønningskortet på Premier (#156, steg 6a; Stell_Premier).
 *
 * Glasskort, radius 24 og minst 128 px høyt:
 *   - kategorichipen øverst (Tid / Opplevelse / Mat / Annet), tonet etter
 *     kategori;
 *   - tittelen (15/800) og en eventuell beskrivelse;
 *   - nederst enten prisen og «Bytt» (barnet har råd), eller prisen, «N igjen»
 *     og en `ProgressBar` fra saldo mot pris. Kortet har da stiplet kant, så
 *     forskjellen ikke bare sitter i fargen. Før saldoen er kjent står bare
 *     prisen.
 *
 * Kategoritoner (skissens `cats`): tid → blå, opplevelse → lilla, mat → amber,
 * annet → nøytral. REWARD_CATEGORIES fra @stell/shared er domenet.
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { REWARD_CATEGORIES } from '@stell/shared';
import { Button } from '../../components/primitives/Button';
import { GLASS_FILL } from '../../components/primitives/Card';
import { Pill, type PillVariant } from '../../components/primitives/Pill';
import { ProgressBar } from '../../components/primitives/ProgressBar';
import { formatPoints } from '../../i18n/formatters';
import type { RewardRow, RewardCategory } from './useRewardsQueries';

const CATEGORY_TONE: Record<RewardCategory, PillVariant> = {
  tid: 'blue',
  opplevelse: 'lilac',
  mat: 'warn',
  annet: 'neutral',
};

// Statisk vakt — feiler høyt om en kategori kommer til uten tone.
for (const c of REWARD_CATEGORIES) {
  if (!CATEGORY_TONE[c]) {
    throw new Error(`Missing category tone for ${c}`);
  }
}

interface RewardCardProps {
  reward: RewardRow;
  /** Saldoen til den innloggede — avgjør «Bytt» eller fremdrift. `undefined` før den er hentet. */
  balance: number | undefined;
  /** Kalles ved trykk på «Bytt» (bare når saldo ≥ pris). */
  onClaim: () => void;
}

export function RewardCard({ reward, balance, onClaim }: RewardCardProps): ReactNode {
  const { t } = useTranslation();
  const known = balance !== undefined;
  const affordable = known && balance >= reward.point_cost;
  const price = t('rewards.points', { points: formatPoints(reward.point_cost) });

  return (
    <article
      data-testid="reward-card"
      data-category={reward.category}
      data-affordable={affordable}
      className={[
        GLASS_FILL,
        'flex min-h-32 flex-col gap-2 rounded-lg border p-3.5 shadow-md',
        known && !affordable ? 'border-dashed border-secondary-line' : 'border-card-line',
      ].join(' ')}
    >
      {/* reward-card-icon var kategoriflisen; chipen er det tilsvarende elementet. */}
      <span data-testid="reward-card-icon" data-category={reward.category} className="self-start">
        <Pill variant={CATEGORY_TONE[reward.category]}>
          {t(`rewards.admin.categoryLabel.${reward.category}`)}
        </Pill>
      </span>

      <h2 className="m-0 text-body leading-tight font-extrabold text-ink">{reward.title}</h2>
      {reward.description ? (
        <p className="m-0 line-clamp-2 text-xs text-ink-2">{reward.description}</p>
      ) : null}

      {!known || affordable ? (
        <div className="mt-auto flex min-h-11 items-center justify-between gap-2">
          <span className="tnum text-body font-extrabold text-ink">{price}</span>
          {affordable ? (
            <Button size={44} data-testid="reward-card-claim" onClick={onClaim}>
              {t('rewards.action.swap')}
            </Button>
          ) : null}
        </div>
      ) : (
        <div className="mt-auto flex flex-col gap-1.5">
          <div className="flex justify-between gap-2 text-xs font-extrabold">
            <span className="tnum text-ink">{price}</span>
            <span data-testid="reward-card-need-more" className="tnum text-accent-text">
              {t('rewards.card.remaining', { count: formatPoints(reward.point_cost - balance) })}
            </span>
          </div>
          <ProgressBar
            value={balance}
            max={reward.point_cost}
            label={t('rewards.card.progress', {
              balance: formatPoints(balance),
              cost: formatPoints(reward.point_cost),
            })}
            data-testid="reward-card-progress"
          />
        </div>
      )}
    </article>
  );
}
